<script setup>
import { ref, computed, watch, nextTick, reactive, onMounted } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useProductStore } from '@/stores/productsStore';
import { useToast } from 'vue-toastification';
import { useBarcodeLabel } from '@/composables/useBarcodeLabel';
import { isOnline } from '@/services/healthCheck';
import axios from 'axios';

const authStore = useAuthStore();
const productStore = useProductStore();
const toast = useToast();
const { printProduct, isPrinting } = useBarcodeLabel();

const singleRow = reactive({ price: '' });

const toPrint = reactive({
  sku: '',
  barcode: '',
  price: 0,
  name: '',
});

const sku = ref('');
const skuInput = ref(null);
const rows = ref([
]);

onMounted(() => {
  if (authStore.printLabelPopupVisible) {
    nextTick(() => {
      if (skuInput.value) {
        skuInput.value.focus();
      }
    });
  }
});

const addRow = () => {
  rows.value.push({ qty: '', price: '' });
};

const removeRow = (index) => {
  if (index < 0 || index >= rows.value.length) return;
  rows.value.splice(index, 1);
};

const validateRows = () => {
  if (!sku.value.trim()) {
    toast.error('SKU is required');
    return false;
  }

  // Single-label mode (no rows): validate `singleRow.price`
  if (rows.value.length === 0) {
    const price = parseFloat(singleRow.price);
    if (!price || isNaN(price) || price <= 0) {
      toast.error('Invalid price');
      return false;
    }
    return true;
  }

  // Bulk mode (1+ rows): validate each row's qty and price
  for (let i = 0; i < rows.value.length; i++) {
    const row = rows.value[i];
    if (!row.qty || isNaN(row.qty) || parseInt(row.qty) <= 0) {
      toast.error(`Invalid quantity in row ${i + 1}`);
      return false;
    }
    if (!row.price || isNaN(row.price) || parseFloat(row.price) <= 0) {
      toast.error(`Invalid price in row ${i + 1}`);
      return false;
    }
  }

  return true;
};

const isGenerating = ref(false);

const isGenerateDisabled = computed(() => {
  if (!sku.value || !sku.value.trim()) return true;
  if (rows.value.length === 0) {
    const price = parseFloat(singleRow.price);
    return !price || isNaN(price) || price <= 0;
  }
  for (const row of rows.value) {
    if (!row.qty || isNaN(row.qty) || parseInt(row.qty) <= 0) return true;
    if (!row.price || isNaN(row.price) || parseFloat(row.price) <= 0) return true;
  }
  return false;
});

const closePopup = () => {
  authStore.togglePrintLabelPopup();
};

const clear = () => {
  sku.value = '';
  rows.value = [];
  singleRow.price = '';
  nextTick(() => {
    if (skuInput.value) {
      skuInput.value.focus();
    }
  });
};

const getCachedProduct = (key) => {
  const cachedProducts = JSON.parse(localStorage.getItem('cachedProducts') || '{}');
  return cachedProducts[key];
};

const cacheProduct = (key, product) => {
  const cachedProducts = JSON.parse(localStorage.getItem('cachedProducts') || '{}');
  cachedProducts[key] = product;
  localStorage.setItem('cachedProducts', JSON.stringify(cachedProducts));
};

const findProductLocally = (query) => {
  const normalized = query.trim().toLowerCase();
  return productStore.products.find((product) => {
    const skuMatch = product?.Sku?.toLowerCase() === normalized;
    const retailMatch = product?.RetailSku?.toLowerCase() === normalized;
    const barcodeMatch = Array.isArray(product?.Barcodes) && product.Barcodes.some((bc) => bc?.toLowerCase() === normalized);
    return skuMatch || retailMatch || barcodeMatch;
  });
};

const mapBackendProduct = (rawProduct) => {
  if (!rawProduct) return null;
  return {
    Barcodes: rawProduct.Barcodes,
    Sku: rawProduct.Sku,
    RetailSku: rawProduct.RetailSku,
    Name: rawProduct.ItemName || rawProduct.Name,
    ImageUrl: rawProduct.ImageUrl,
    MaxQty: rawProduct.Quantity ?? rawProduct.MaxQty,
    Price: rawProduct.Price,
    Discount: rawProduct.DiscountPrice,
    isNonStocking: rawProduct.NonStocking,
    Comment: rawProduct.Comment,
  };
};

const fetchProductFromBackend = async (query) => {
  const token = authStore.managerToken || authStore.token || localStorage.getItem('managerToken') || localStorage.getItem('token');
  const branchId = authStore.branchId || localStorage.getItem('branchId');

  if (!token) {
    toast.error('Authentication token is missing. Please log in again.');
    return null;
  }

  const headers = {
    'store-id': branchId,
    Authorization: `Bearer ${token}`,
  };

  try {
    const response = await axios.get(`/api/Order/GetProductByRetailSkuSingle?retailSku=${query}`, { headers });
    if (response.data?.Status === 200 && response.data?.Product) {
      return mapBackendProduct(response.data.Product);
    }
  } catch (error) {
    console.error('Product lookup error:', error);
    toast.error('Failed to fetch product info');
  }

  return null;
};

const resolveProductForLabel = async () => {
  const query = sku.value?.trim();
  if (!query) {
    toast.error('SKU is required');
    return null;
  }

  const localProduct = findProductLocally(query);
  if (localProduct) return localProduct;

  const cachedProduct = getCachedProduct(query);
  if (cachedProduct) return cachedProduct;

  const backendProduct = await fetchProductFromBackend(query);
  if (backendProduct) {
    // cacheProduct(query, backendProduct);
    return backendProduct;
  }

  return null;
};

const ensureProductBeforeGenerate = async () => {
  const product = await resolveProductForLabel();
  if (!product) {
    toast.error('Item not found locally or on server.');
    return null;
  }

  if (isGenerateDisabled.value) {
    toast.error('Enter a valid SKU and price before generating.');
    return null;
  }

  return product;
};

// Generate label api call handler

const generateLabel = async (product) => {
  const token = localStorage.getItem('token') || localStorage.getItem('managerToken');
  if (!token) {
    console.log('Authentication token is missing. Please log in again.');
    return;
  }
  
  if (!isOnline.value) {
    toast.warning('Generating barcode labels requires an internet connection.');
    return;
  }

  const payload = {
    sku: sku.value,
    price: parseFloat(singleRow.price),
    allowedUses: 1,
  };
  // if (!validateRows()) return;

  isGenerating.value = true;

  try {
    if (payload) {
      const response = await axios.post('/api/Barcode/generate', payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.status === 200) {
        toast.success('Label generated successfully now.');
        isGenerating.value = false;
        toPrint.sku = response.data.sku;
        toPrint.barcode = response.data.barcode;
        toPrint.price = response.data.price;
        const resolvedName = product?.Name || product?.ItemName || response.data?.name || '';
        toPrint.name = resolvedName;
        toPrint.ItemName = resolvedName;
        toPrint.Name = resolvedName;
        await printProduct(toPrint);
        clear();
      } else {
        const apiError = response.data?.error;
        if (apiError) {
          toast.error(apiError);
        } else {
          toast.error(JSON.stringify(response.data || 'Failed to generate label'));
        }
      }
    }
    toast.success('Label generated successfully');
  } catch (error) {
    console.error('Generate label error:', error);
    // If server returned structured error in the response, show it
    const serverData = error?.response?.data;
    const serverMessage = serverData?.error || serverData?.message;
    if (serverMessage) {
      toast.error(serverMessage);
    } else if (serverData) {
      toast.error(JSON.stringify(serverData));
    } else {
      toast.error('Failed to generate label');
    }
  } finally {
    isGenerating.value = false;
  }
};

// Generate bulk label api call handler
const generateBulkLabels = async (product) => {
  const token = localStorage.getItem('token') || localStorage.getItem('managerToken');
  if (!token) {
    console.log('Authentication token is missing. Please log in again.');
    return;
  }
  // Capture qty per row before sending — needed to know how many times to print each barcode.
  // count:1 creates one unique DB record per price row; allowedUses=qty means it can be
  // scanned that many times. We then print the returned barcode qty times.
  const rowQtys = rows.value.map(row => parseInt(row.qty) || 1);
  const bulkPayload = {
    sku: sku.value,
    prices: rows.value.map((row, i) => ({
      count: 1,
      price: parseFloat(row.price),
      allowedUses: rowQtys[i],
    }))
  }
  // if (!validateRows()) return;

  if (!isOnline.value) {
    toast.warning('Generating barcode labels requires an internet connection.');
    return;
  }

  isGenerating.value = true;

  try {
    if (bulkPayload) {
      const response = await axios.post('/api/Barcode/generate-bulk', bulkPayload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.status === 200) {
        toast.success('Bulk labels generated successfully now.');
        isGenerating.value = false;

        const resp = response.data || {};
        const respResults = Array.isArray(resp.results) ? resp.results : [];

        // Each result is one unique barcode. Print it qty times (one physical label per use).
        for (let i = 0; i < respResults.length; i++) {
          const item = respResults[i];
          if (!item?.barcode) continue;
          const printCount = rowQtys[i] ?? 1;
          const productForPrint = {
            sku: item.sku,
            RetailSku: item.sku,
            barcode: item.barcode,
            price: item.price,
            Price: item.price,
            ItemName: product?.Name || product?.ItemName || '',
            Name: product?.Name || product?.ItemName || '',
          };
          for (let p = 0; p < printCount; p++) {
            await printProduct(productForPrint);
          }
        }
        clear();
      } else {
        const apiError = response.data?.error;
        if (apiError) {
          toast.error(apiError);
        } else {
          toast.error(JSON.stringify(response.data || 'Failed to generate bulk labels'));
        }
      }
    }
    toast.success('Bulk labels generated successfully');
  } catch (error) {
    console.error('Generate bulk labels error:', error);
    // If server returned structured error in the response, show it
    const serverData = error?.response?.data;
    const serverMessage = serverData?.error || serverData?.message;
    if (serverMessage) {
      toast.error(serverMessage);
    } else if (serverData) {
      toast.error(JSON.stringify(serverData));
    } else {
      toast.error('Failed to generate bulk labels');
    }
  } finally {
    isGenerating.value = false;
  }
};

const handleGenerateLabel = async () => {
  const product = await ensureProductBeforeGenerate();
  if (!product) return;

  if (rows.value.length >= 1) {
    await generateBulkLabels(product);
  } else {
    await generateLabel(product);
  }
};

// History
const showHistory = ref(false);
const historyData = ref(null);
const isLoadingHistory = ref(false);

const fetchHistory = async () => {
  const query = sku.value?.trim();
  if (!query) {
    toast.error('Enter a SKU first');
    return;
  }
  const token = authStore.managerToken || authStore.token;
  if (!token) return;

  isLoadingHistory.value = true;
  showHistory.value = true;
  historyData.value = null;

  try {
    const response = await axios.get(`/api/Barcode/history?sku=${encodeURIComponent(query)}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    historyData.value = response.data;
  } catch (error) {
    toast.error('Failed to load history');
    showHistory.value = false;
  } finally {
    isLoadingHistory.value = false;
  }
};

const formatDate = (dt) => {
  if (!dt) return '';
  // Treat as UTC if no timezone info is present, then display in local time
  const utc = /[Z+\-]\d*$/.test(dt) ? dt : dt + 'Z';
  return new Date(utc).toLocaleString();
};

const priceValidator = async (e, row) => {
  const el = e?.target;
  const raw = (el?.value ?? row?.price ?? '').toString();
  const caret = typeof el?.selectionStart === 'number' ? el.selectionStart : raw.length;

  let out = '';
  let outCaret = 0;
  let seenDot = false;
  let intDigits = 0;
  let decDigits = 0;

  for (let i = 0; i < raw.length; i++) {
    const c = raw[i];
    const beforeCaret = i < caret;

    if (c >= '0' && c <= '9') {
      if (!seenDot) {
        if (intDigits < 5) {
          out += c;
          intDigits++;
          if (beforeCaret) outCaret++;
        }
      } else {
        if (decDigits < 2) {
          out += c;
          decDigits++;
          if (beforeCaret) outCaret++;
        }
      }
      continue;
    }

    if (c === '.' && !seenDot) {
      // allow leading dot by turning "." -> "0."
      if (out.length === 0) {
        out += '0';
        intDigits = Math.max(intDigits, 1);
        if (beforeCaret) outCaret++;
      }
      out += '.';
      seenDot = true;
      if (beforeCaret) outCaret++;
    }
  }

  row.price = out;

  // Preserve caret position (works reliably with type="text")
  await nextTick();
  if (el && typeof el.setSelectionRange === 'function') {
    const pos = Math.min(outCaret, row.price.length);
    el.setSelectionRange(pos, pos);
  }
};

</script>

<template>
  <Teleport to="body">
    <Transition name="modal-outer">
      <div v-show="authStore.printLabelPopupVisible" 
           @click.self="closePopup"
           class="absolute w-full bg-black bg-opacity-30 h-screen top-0 left-0 flex justify-center px-8 z-[100]">
        <Transition name="modal-inner" class="rounded-2xl">
          <div v-if="authStore.printLabelPopupVisible"
               class="fixed top-20 z-[101] flex items-center justify-center bg-black bg-opacity-50 w-10/12 max-w-2xl">
            <div class="bg-white rounded-2xl shadow-lg p-6 w-full">
              <div class="flex justify-between items-center mb-6">
                <h2 class="font-semibold text-[24px]">Generate Label</h2>
                <button type="button" @click="closePopup" class="text-gray-500 hover:text-gray-700">
                  <i class="pi pi-times text-xl"></i>
                </button>
              </div>

              <div class="mb-6 flex items-end gap-4">
                <div class="flex-1">
                  <label class="block text-sm font-medium text-gray-700 mb-1">SKU</label>
                  <div class="flex gap-2">
                    <input
                      @keydown="(e) => { if (e.key === '.') e.preventDefault(); }"
                      ref="skuInput"
                      v-model="sku"
                      type="text"
                      placeholder="Enter SKU"
                      class="flex-1 px-4 py-3 bg-gray-100 border-none rounded-lg outline-none"
                    />
                    <button
                      v-if="sku.trim()"
                      type="button"
                      @click="fetchHistory"
                      class="px-3 py-3 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                      title="View barcode history"
                    >
                      <i class="pi pi-history"></i>
                    </button>
                  </div>
                </div>
                <div v-if="rows.length === 0" class="w-1/3">
                  <label class="block text-sm font-medium text-gray-700 mb-1">PRICE</label>
                  <input
                    v-model="singleRow.price"
                    type="text"
                    inputmode="decimal"
                    autocomplete="off"
                    placeholder="Price"
                    class="w-full px-4 py-3 bg-gray-100 border-none rounded-lg outline-none"
                    @input="priceValidator($event, singleRow)"
                  />
                </div>
              </div>

              <div v-if="rows.length >= 1" class="max-h-[40vh] overflow-y-auto mb-6">
                <div v-for="(row, index) in rows" :key="index" class="flex gap-4 mb-3 items-end">
                  <div class="flex-1">
                    <label v-if="index === 0" class="block text-sm font-medium text-gray-700 mb-1">QTY</label>
                    <input
                      @keydown="(e) => { if (e.key === '.') e.preventDefault(); }"
                      v-model="row.qty"
                      type="number"
                      placeholder="Qty"
                      class="w-full px-4 py-2 bg-gray-100 border-none rounded-lg outline-none"
                      @input="row.qty = row.qty?.toString().slice(0, 2)"
                    />
                  </div>
                  <div class="flex-1">
                    <label v-if="index === 0" class="block text-sm font-medium text-gray-700 mb-1">PRICE</label>
                    <input
                      v-model="row.price"
                      type="text"
                      inputmode="decimal"
                      autocomplete="off"
                      placeholder="Price"
                      class="w-full px-4 py-2 bg-gray-100 border-none rounded-lg outline-none"
                      @input="priceValidator($event, row)"
                    />
                  </div>
                  <button
                    type="button"
                    @click="removeRow(index)"
                    class="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <i class="pi pi-trash"></i>
                  </button>
                </div>
                
              </div>
              <button 
                  type="button"
                  @click="addRow" 
                  class="my-2 text-purple-600 font-medium flex items-center gap-2 hover:text-purple-700"
                >
                  <i class="pi pi-plus-circle"></i> Add Row
              </button>
              <div class="flex gap-4">
                <button
                  type="button"
                  @click="clear"
                  class="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                >
                  Clear
                </button>
                <button
                  type="button"
                  @click="handleGenerateLabel"
                  :disabled="isGenerating || isGenerateDisabled"
                  class="flex-[2] px-4 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors font-medium disabled:opacity-50"
                >
                  <span v-if="!isGenerating">Generate & Print Barcodes</span>
                  <span v-else>Printing...</span>
                </button>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>

    <!-- History Modal -->
    <Transition name="modal-outer">
      <div v-if="showHistory"
           @click.self="showHistory = false"
           class="absolute w-full bg-black bg-opacity-30 h-screen top-0 left-0 flex justify-center px-8 z-[110]">
        <div class="fixed top-10 z-[111] w-11/12 max-w-4xl max-h-[85vh] flex flex-col bg-white rounded-2xl shadow-lg">
          <div class="flex justify-between items-center p-6 border-b border-gray-100">
            <h2 class="font-semibold text-[20px]">Barcode History — {{ sku }}</h2>
            <button type="button" @click="showHistory = false" class="text-gray-500 hover:text-gray-700">
              <i class="pi pi-times text-xl"></i>
            </button>
          </div>

          <div class="overflow-y-auto flex-1 p-6">
            <div v-if="isLoadingHistory" class="flex justify-center py-12">
              <i class="pi pi-spin pi-spinner text-2xl text-purple-600"></i>
            </div>

            <div v-else-if="!historyData || !historyData.barcodes || historyData.barcodes.length === 0"
                 class="text-center text-gray-500 py-12">
              No barcodes found for this SKU.
            </div>

            <div v-else class="space-y-6">
              <div v-for="bc in historyData.barcodes" :key="bc.id"
                   class="border border-gray-200 rounded-xl overflow-hidden">
                <!-- Barcode header row -->
                <div class="bg-gray-50 px-4 py-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                  <span class="font-mono font-bold text-purple-700">{{ bc.barcode }}</span>
                  <span><span class="text-gray-500">Price:</span> ${{ bc.price.toFixed(2) }}</span>
                  <span><span class="text-gray-500">Uses:</span> {{ bc.usedCount }} / {{ bc.allowedUses }}</span>
                  <span><span class="text-gray-500">Created:</span> {{ formatDate(bc.createdAt) }}</span>
                  <span><span class="text-gray-500">By:</span> {{ bc.createdBy || '—' }}</span>
                </div>

                <!-- Usages sub-table -->
                <div v-if="bc.usages && bc.usages.length > 0" class="overflow-x-auto">
                  <table class="w-full text-sm">
                    <thead>
                      <tr class="text-left text-gray-500 border-b border-gray-100">
                        <th class="px-4 py-2 font-medium">Receipt</th>
                        <th class="px-4 py-2 font-medium">Used By</th>
                        <th class="px-4 py-2 font-medium">Used At</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(usage, ui) in bc.usages" :key="ui"
                          class="border-b border-gray-50 hover:bg-gray-50">
                        <td class="px-4 py-2 font-mono text-xs">{{ usage.receiptNumber }}</td>
                        <td class="px-4 py-2">{{ usage.usedBy || '—' }}</td>
                        <td class="px-4 py-2 text-gray-600">{{ formatDate(usage.usedAt) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="px-4 py-2 text-sm text-gray-400 italic">Not yet used</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-outer-enter-active,
.modal-outer-leave-active {
  transition: opacity 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12);
}

.modal-outer-enter-from,
.modal-outer-leave-to {
  opacity: 0;
}

.modal-inner-enter-active {
  transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12) 0.15s;
}

.modal-inner-leave-active {
  transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12);
}

.modal-inner-enter-from {
  opacity: 0;
  transform: scale(0.8);
}

.modal-inner-leave-to {
  transform: scale(0.8);
}

/* Remove arrows from number inputs */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
input[type=number] {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>